/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable import/order */
import {
  distanceInWordsToNow,
  distanceInWordsStrict,
  isValid,
  addMonths,
  startOfDay,
  endOfDay,
  isAfter,
  startOfYear,
  startOfWeek,
  endOfWeek,
  eachDay,
  isBefore
} from 'date-fns';

import en from 'date-fns/locale/en';
import es from 'date-fns/locale/es';
import fr from 'date-fns/locale/fr';
import pt from 'date-fns/locale/pt';

import 'moment-timezone';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/pt-br';

import { French } from 'flatpickr/dist/l10n/fr';
import { Portuguese } from 'flatpickr/dist/l10n/pt';
import { Spanish } from 'flatpickr/dist/l10n/es';

import moment from 'moment';
import isNumber from 'lodash/isNumber';
import isDate from 'lodash/isDate';
import clamp from 'lodash/clamp';
import isNil from 'lodash/isNil';
import { isString, padStart, capitalize } from './String';

import { UserService } from 'modules/user';
import DateHelper from './Date';

const REGEXES = {
  ISO: new RegExp(
    [
      '^(-?(?:[1-9][0-9]*)?[0-9]{4})-(1[0-2]|0[1-9])-(3[01]|0[1-9]|[12][0-9])',
      'T(2[0-3]|[01][0-9]):([0-5][0-9]):([0-5][0-9])(.[0-9]+)?(Z)?$'
    ].join('')
  ),
  DDMMYYYY: /(\d{1,2})[\/-]?(\d{1,2})[\/-]?(\d{4})/
};

export const FORMATS = {
  ISO: 'YYYY-MM-DD[T]HH:mm:ssZZ',
  DDMMYYYY: 'DD/MM/YYYY',
  DDMMYYYY_HHMM: 'DD/MM/YYYY HH:mm',
  MMDDYYYY: 'MM/DD/YYYY',
  DDMMYY: 'DD/MM/YY',
  MMDDYY: 'MM/DD/YY'
};

export const setDaysLabels = {
  'start-date-placeholder': 'date_picker.start_date_placeholder',
  'end-date-placeholder': 'date_picker.end_date_placeholder',
  'last': 'general.last',
  '1d': 'date_picker.1d',
  '7d': 'date_picker.7d',
  '15d': 'date_picker.15d',
  '30d': 'date_picker.30d',
  '45d': 'date_picker.45d',
  '3m': 'date_picker.3m',
  '6m': 'date_picker.6m',
  '12m': 'date_picker.12m',
  '18m': 'date_picker.18m',
  '24m': 'date_picker.24m',
  'cancel': 'general.cancel',
  'apply': 'general.apply',
  'last-1d': 'date_picker.last_1d',
  'last-7d': 'date_picker.last_7d',
  'last-15d': 'date_picker.last_15d',
  'last-30d': 'date_picker.last_30d',
  'last-45d': 'date_picker.last_45d',
  'last-3m': 'date_picker.last_3m',
  'last-6m': 'date_picker.last_6m',
  'last-12m': 'date_picker.last_12m',
  'last-18m': 'date_picker.last_18m',
  'last-24m': 'date_picker.last_24m'
};

/**
 * Check if current language is English
 * @returns {Boolean}
 */
const isLanguageEnglish = () => UserService.language === 'EN';

/**
 * Check if it is a valid year
 * @param {Number} year - Numeric value for year [1970, ...];
 */
const isValidYear = (year) => year >= 1970;

/**
 * Check if it is a valid month
 * @param {Number} month - Numeric value for month [1, 12];
 */
const isValidMonth = (month) => month <= 12 && month >= 1;

/**
 * Check if it is a valid day
 * @param {Number} day - Numeric value for day [1, 31];
 */
const isValidDay = (day) => day <= 31 && day >= 1;

/**
 * Convert a timestamp to *ClubDate*.
 * @param {Number} timestamp - Timestamp to be converted.
 * @returns {ClubDate}
 */
const fromTimestamp = (timestamp) => {
  return new Date(timestamp);
};

/**
 * Convert from *Moment* to *ClubDate*.
 * @param {Moment} moment - Moment date object.
 * @returns {ClubDate}
 */
const fromMoment = (momentDate) => {
  return momentDate.toDate();
};

/**
 * Parse a string date to *ClubDate*.
 * Valid formats are:
 * @param {Number} timestamp - Timestamp to be converted.
 * @param {Boolean} englishFormat - Expect English date format.
 * @returns {ClubDate}
 */
const fromString = (value, englishFormat = false) => {
  const asString = `${value}`;

  if (REGEXES.ISO.test(asString)) {
    return new Date(asString);
  }

  const parts = asString.match(REGEXES.DDMMYYYY);

  if (!parts) {
    return null;
  }

  const yearIndex = 3;
  const [monthIndex, dayIndex] = (() => {
    if (isLanguageEnglish() || englishFormat) {
      return [1, 2];
    }

    return [2, 1];
  })();

  const year = parseInt(parts[yearIndex], 10);
  const month = parseInt(parts[monthIndex], 10);
  const day = parseInt(parts[dayIndex], 10);

  if (!isValidDay(day) || !isValidMonth(month) || !isValidYear(year)) {
    return null;
  }

  return new Date(year, month - 1, day, 0, 0, 0);
};

/**
 * Apply a specific hour/minute/seconds or milliseconds to the given Date.
 * @param {Date} date - Date to be set.
 * @param {Object} time - Time to be applied to the given date.
 * @param {Number} time.h - Hour to be set.
 * @param {Number} time.m - Minutes to be set.
 * @param {Number} time.s - Seconds to be set (default to 0).
 * @param {Number} time.ms - Millisseconds to be set (default to 0).
 */
const setTime = (date, { h, m, s = 0, ms = 0 }) => {
  if (!date) {
    return date;
  }

  if (!isNil(h)) {
    date.setHours(clamp(h, 0, 23));
  }

  if (!isNil(m)) {
    date.setMinutes(clamp(m, 0, 59));
  }

  if (!isNil(s)) {
    date.setSeconds(clamp(s, 0, 59));
  }

  if (!isNil(ms)) {
    date.setMilliseconds(clamp(ms, 0, 999));
  }

  return date;
};

/**
 * This class has the purpose to concentrate all date transformations
 * needed across Club Admin application.
 * It allows conversion from/to Date, timestamp, string formatted (DD/MM/YYYY, MM/DD/YYYY,
 * DDMMYYYY, MMDDYYYY, ISO formatted).
 * You can do some operations like:
 *
 * @example
 * ClubDate.from.timestamp(new Date().getTime()).to.toLocaleDateString());
 * ClubDate.from.string('07/04/2019').to.localeDateString('en-GB');
 * ClubDate.resolve('07/04/2019').to.timestamp();
 * ClubDate.resolve(1554606000000).to.ISOString();
 */
// eslint-disable-next-line padded-blocks
class ClubDate {
  /**
   * Return moment's specific code for the currently user language
   * or pt-BR as the default language.
   * @returns {String}
   */
  static get language() {
    const languages = {
      EN: 'en',
      ES: 'es',
      FR: 'fr',
      PT_BR: 'pt-BR'
    };

    return languages[UserService.language] || languages.PT_BR;
  }

  /**
   * Return current supported locales (for dates).
   * @returns {Object} Locales mapped by their acronyms.
   */
  static get locale() {
    const locales = {
      EN: en,
      ES: es,
      FR: fr,
      PT_BR: pt
    };

    return locales[UserService.language] || pt;
  }

  /**
   * Return current supported locales (for dates).
   * @returns {Object} Locales mapped by their acronyms.
   */
  static get CosmosLocale() {
    const locales = {
      ES: Spanish,
      FR: French,
      PT_BR: Portuguese
    };

    return locales[UserService.language] || pt;
  }

  /**
   * Convert seconds to String in a [HH]:mm:seconds format.
   * @param {Number} seconds - Amount of seconds.
   * @param {Boolean} [renderHour=false] - Should show the optional hours (HH) part.
   *
   * @returns {String}
   */
  static secondsToHms(seconds, renderHour = false) {
    const pad = (text) => padStart(text, 2, '0');

    seconds = Number(seconds);

    if (Number.isNaN(seconds)) {
      return 0;
    }

    const rawH = Math.floor(seconds / 3600);
    const rawM = Math.floor((seconds % 3600) / 60);
    const rawS = Math.floor((seconds % 3600) % 60);

    const formattedH = pad(rawH);
    const formattedM = pad(rawM);
    const formattedS = pad(rawS);

    let pieces = [formattedM, formattedS];

    if (renderHour) {
      pieces = [formattedH, ...pieces];
    }

    return pieces.join(':');
  }

  static convertUTCDateToLocalDate(date) {
    const MINUTES_IN_HOUR = 60;

    const newDate = new Date(date);
    const offset = newDate.getTimezoneOffset() / MINUTES_IN_HOUR;
    const hours = newDate.getHours();

    newDate.setHours(hours + offset);

    return newDate;
  }

  /**
   * Check if a Date instance represents a valid date.
   * @param {Date} date - Date to evaluated.
   *
   * @returns {Boolean} _true_ if valid, _false_ otherwise.
   */
  static isValid(date) {
    return isValid(date);
  }

  /**
   * Format the given Date.
   * @param {Date} date - Date to formatted.
   * @param {String} [format] - Format to be applied, which will be localized according to current language (https://date-fns.org/v1.30.1/docs/format).
   * @returns {String} Formatted date.
   */
  static format(date, format = FORMATS.DDMMYYYY, options) {
    return date ? DateHelper.format(date, format, options) : '';
  }

  /**
   * Return current supported locales (for dates).
   * @returns {Object} Locales mapped by their acronyms.
   */
  static get locales() {
    return {
      en,
      es,
      pt,
      fr
    };
  }

  /**
   * Return the distance between the given dates in words, using strict units.
   * @param {Date} firstDate - Initial date.
   * @param {Date} secondDate - Final date.
   * @param {Object} options - Additional options (Check https://date-fns.org/v1.30.1/docs/distanceInWordsStrict).
   * @returns {String} Distance in words.
   */
  static distanceInWords(firstDate, secondDate, options) {
    return distanceInWordsStrict(firstDate, secondDate, options);
  }

  /**
   * Return the distance between the given date and now in words, using strict units.
   * @param {Date} date - Initial date.
   * @param {Object} options - Additional options (Check https://date-fns.org/v1.30.1/docs/distanceInWordsToNow).
   * @returns {String} Distance in words.
   */
  static distanceInWordsToNow(date, options) {
    return distanceInWordsToNow(date, options);
  }

  /**
   * Check if the first date is after the second.
   * @param {Date} first - First date
   * @param {Date} second - Second date
   * @returns {Boolean} True if first date is after the second; false, otherwise.
   */
  static isAfter(first, second) {
    return isAfter(first, second);
  }

  /**
   * Check if the first date is before the second.
   * @param {Date} first - First date
   * @param {Date} second - Second date
   * @returns {Boolean} True if first date is before the second; false, otherwise.
   */
  static isBefore(first, second) {
    return isBefore(first, second);
  }

  /**
   * Resolve the given _date_ to ClubDate type, which allows several types
   * of conversions and format operations.
   * @param {Date|Number|String} value - Value to be converted to ClubDate.
   * @returns {ClubDate} Resolved date.
   */
  static resolve(value, ...args) {
    let date = null;

    if (isNil(value)) {
      return null;
    }

    if (isNumber(value)) {
      date = fromTimestamp(value, ...args);
    } else if (isString(value)) {
      date = fromString(value, ...args);
    } else if (moment.isMoment(value)) {
      date = fromMoment(value);
    } else if (isDate(value)) {
      date = value;
    }

    if (date) {
      return new ClubDate(date);
    }

    return null;
  }

  static from = {
    timestamp: (value, ...args) => new ClubDate(fromTimestamp(value, ...args)),
    string: (value, ...args) => new ClubDate(fromString(value, ...args)),
    moment: (value, ...args) => new ClubDate(fromMoment(value, ...args))
  };

  get to() {
    return {
      /**
       * Return JS native Date object.
       * @returns {Date}
       */
      date: () => this.date,

      /**
       * Return number of milliseconds elapsed since January 1, 1970 00:00:00 UTC.
       * *Ex.: 1562278671388*
       * @returns {String}
       */
      timestamp: () => this.date && this.date.getTime(),

      /**
       * Return ISO formatted date.
       * *Ex.: 2019-01-23T09:23:42.079Z*
       * @returns {String}
       */
      ISOString: () => this.date && this.date.toISOString(),

      /**
       * Return UTC formatted date.
       * *Ex.: Wed, 04 Jul 2019 09:23:42 GMT*
       * @returns {String}
       */
      UTCString: () => this.date && this.date.toUTCString(),

      /**
       * Return string with a language sensitive representation of the date portion of this date.
       * *Ex.: 04/07/2019 (DD/MM/YYYY)*
       * @returns {String}
       */
      localeDateString: (...args) =>
        this.date && this.date.toLocaleDateString(...args),

      /**
       * Return string with a language sensitive representation of the time portion of this date.
       * *Ex.: 1:15:30 AM (HH:MM:SS)*
       * @returns {String}
       */
      localeTimeString: (...args) =>
        this.date && this.date.toLocaleTimeString(...args),

      /**
       * Return number of seconds elapsed since January 1, 1970 00:00:00 UTC.
       * *Ex.: 156227867*
       * @returns {String}
       */
      seconds: () => Math.floor(this.to.timestamp() / 1000)
    };
  }

  get at() {
    return {
      /**
       * Apply a specific hour/minute/seconds or milliseconds to the given Date.
       * @param {Date} date - Date to be set.
       * @param {Object} time - Time to be applied to the given date.
       * @param {Number} time.h - Hour to be set.
       * @param {Number} time.m - Minutes to be set.
       * @param {Number} time.s - Seconds to be set (default to 0).
       * @param {Number} time.ms - Millisseconds to be set (default to 0).
       * @returns {ClubDate}
       */
      time: (time = { s: 0, ms: 0 }) => {
        this.date = setTime(this._date, time);

        return this;
      },

      /**
       * Return the end of a day for the given date. The result will be in the local timezone.
       * @returns {ClubDate}
       */
      endOfDay: () => {
        this.date = endOfDay(this._date);

        return this;
      },

      /**
       * Return the start of a day for the given date. The result will be in the local timezone.
       * @returns {ClubDate}
       */
      startOfDay: () => {
        this.date = startOfDay(this._date);

        return this;
      }
    };
  }

  constructor(date) {
    this.date = date || new Date();
  }

  set date(date) {
    if (!ClubDate.isValid(date)) {
      throw new Error(`[${date}] is not a valid date!`);
    }

    this._date = date;
  }

  get date() {
    return this._date;
  }

  /**
   * Format the given Date.
   * @param {Date} date - Date to formatted.
   * @param {String} [format] - Format to be applied. Default is DD/MM/YYYY (or MM/DD/YYYY if current language is English) (according to https://date-fns.org/v1.30.1/docs/format).
   * @returns {Boolean} _true_ if valid, _false_ otherwise.
   */
  format(format, options) {
    return ClubDate.format(this._date, format, options);
  }

  /**
   * Return the distance between this date and the given date in words, using strict units.
   * @param {Date} dateToCompare - Final date.
   * @param {Object} [options] - Additional options (Check https://date-fns.org/v1.30.1/docs/distanceInWordsStrict).
   * @returns {String} Distance in words.
   */
  distanceInWords(dateToCompare, options) {
    return distanceInWordsStrict(this._date, dateToCompare, options);
  }

  /**
   * Return the distance between this date and now in words, using strict units.
   * @param {Object} [options] - Additional options (Check https://date-fns.org/v1.30.1/docs/distanceInWordsToNow).
   * @returns {String} Distance in words.
   */
  distanceInWordsToNow(options) {
    return distanceInWordsToNow(this._date, options);
  }

  /**
   * Check if the first date is after the second.
   * @param {Date} first - First date
   * @param {Date} second - Second date
   * @returns {Boolean} True if first date is after the second; false, otherwise.
   */
  isAfter(date) {
    return ClubDate.isAfter(this._date, date);
  }

  /**
   * Check if the first date is before the second.
   * @param {Date} first - First date
   * @param {Date} second - Second date
   * @returns {Boolean} True if first date is before the second; false, otherwise.
   */
  isBefore(date) {
    return ClubDate.isBefore(this._date, date);
  }

  toString() {
    if (!this._date) {
      return '';
    }

    return this.date.toString();
  }

  static get weekdays() {
    const now = new Date();
    const week = eachDay(startOfWeek(now), endOfWeek(now));

    return week.reduce((acc, idx) => {
      acc.push(ClubDate.format(idx, 'ddd', { locale: this.locale }));
      return acc;
    }, []);
  }

  static get months() {
    const startYear = startOfYear(new Date());

    const months = Array(12)
      .fill(0)
      .map((_, idx) =>
        capitalize(
          ClubDate.format(addMonths(startYear, idx), 'MMMM', {
            locale: this.locale
          })
        )
      );

    return months;
  }

  static formatDateToUtc = (date, zoneId) => {
    try {
      const dateTime = date ? date : new Date();
      const zone = zoneId ? zoneId : moment.tz.guess();
      const formatTimezone = moment(dateTime).tz(zone).format();
      const dateFormated = moment.utc(formatTimezone).format();

      return dateFormated;
    } catch {
      return '';
    }
  };
}

export default ClubDate;
